import { Button } from '@/components/ui/button'
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import type { Member } from '@stytch/vanilla-js'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface UserRecordPanelProps {
	onClose: () => void
	user?: Member
	isNew?: boolean
}

export function UserRecordPanel({ onClose, user, isNew = false }: UserRecordPanelProps) {
	const stytchClient = useStytchB2BClient()
	const { member: currentMember } = useStytchMember()
	const [name, setName] = useState('')
	const [email, setEmail] = useState('')
	const [isSaving, setIsSaving] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)

	// Initialize form when user changes
	useEffect(() => {
		if (user) {
			setName(user.name || '')
			setEmail(user.email_address || '')
		} else if (isNew) {
			setName('')
			setEmail('')
		}
	}, [user, isNew])

	const handleSave = async () => {
		if (!currentMember) return

		// Validate email format for new users
		if (isNew && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			toast.error('Please enter a valid email address')
			return
		}

		if (isNew && !email.trim()) {
			toast.error('Email is required')
			return
		}

		setIsSaving(true)
		try {
			if (isNew) {
				// Invite new user
				await stytchClient.organization.members.create({
					email_address: email,
					name: name || undefined,
				})
				toast.success('User invited successfully')
			} else if (user) {
				// Update existing user
				await stytchClient.organization.members.update({
					member_id: user.member_id,
					name: name || undefined,
				})
				toast.success('User updated successfully')
			}
			onClose()
		} catch (error) {
			console.error('Failed to save user:', error)
			toast.error('Failed to save user')
		} finally {
			setIsSaving(false)
		}
	}

	const handleDelete = async () => {
		if (!user) return

		setIsDeleting(true)
		try {
			await stytchClient.organization.members.delete(user.member_id)
			toast.success('User removed successfully')
			onClose()
		} catch (error) {
			console.error('Failed to delete user:', error)
			toast.error('Failed to remove user')
		} finally {
			setIsDeleting(false)
		}
	}

	const titleText = isNew ? 'Add User' : user?.name || 'Edit User'

	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>{titleText}</DrawerTitle>
				<DrawerClose asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="hover:bg-primary hover:text-primary-foreground"
						title={isNew ? 'Cancel Add User' : 'Cancel Edit User'}
						aria-label={isNew ? 'Cancel Add User' : 'Cancel Edit User'}
					>
						<X className="h-4 w-4" />
					</Button>
				</DrawerClose>
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="user-name">Name</Label>
						<Input
							id="user-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Enter full name"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="user-email">Email</Label>
						<Input
							id="user-email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="user@company.com"
							disabled={!isNew}
						/>
						{!isNew && (
							<p className="text-xs text-muted-foreground">
								Email changes require verification. Contact your admin.
							</p>
						)}
					</div>

					{user && (
						<div className="space-y-2">
							<Label>Status</Label>
							<div className="flex items-center gap-2">
								<span className="text-sm font-medium capitalize">{user.status}</span>
								{user.roles && user.roles.length > 0 && (
									<span className="text-sm text-muted-foreground">• {user.roles[0].role_id}</span>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			<DrawerFooter className="space-y-3">
				{!isNew && user?.member_id !== currentMember?.member_id && (
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={isDeleting || isSaving}
						className="w-full"
					>
						{isDeleting ? 'Deleting...' : 'Delete'}
					</Button>
				)}
				<Button onClick={handleSave} disabled={isSaving || isDeleting} className="w-full">
					{isSaving ? 'Saving...' : isNew ? 'Add User' : 'Save Changes'}
				</Button>
			</DrawerFooter>
		</div>
	)
}
